import { useOidc } from '@axa-fr/react-oidc';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { getActiveProvider } from '../auth/config';
import { looksLikeEmail, providerForEmail } from '../auth/emailRouting';
import { DHI_ENTRA, MIKE_CLOUD } from '../auth/oidc';
import type { AuthProviderConfig } from '../types/api';
import { SUPPORT_URL } from '../utils/support';
import styles from './LoginScreen.module.css';
import TopoBackground from './TopoBackground';

/**
 * Shown for the moment between the code exchange finishing and the app taking over.
 *
 * Ours rather than the library's, whose default is an unstyled line of English. It should
 * be a blink; if it is ever on screen long enough to read, the hand-off after the callback
 * has failed and the wording should not promise a redirect that is not coming.
 */
export function SigningIn() {
  return (
    <div className={styles.view}>
      <img className={styles.logo} src={`${import.meta.env.BASE_URL}dhi-logo.png`} alt="DHI" />
      <div className={styles.content}>
        <h1 className={styles.title}>
          Docs<em className={styles.titleAccent}> AI</em>
        </h1>
        <p className={styles.subtitle}>Signing you in...</p>
      </div>
    </div>
  );
}

/**
 * How long the redirect gets before this screen offers a way out of it.
 *
 * In the ordinary case the screen below is a blink on the way to MIKE Cloud, and a
 * "not redirected?" link rendered immediately would flash on every single sign-in,
 * reading as a warning about a login that is working. It is only worth showing once the
 * blink has failed to be a blink.
 */
const STUCK_AFTER_MS = 3000;

/**
 * Shown while the library is redirecting to the identity provider, in place of its own
 * default.
 *
 * That default is two lines of unstyled English on our own origin: "Authentication in
 * progress" over "You will be redirected to the login page." Replaced for the obvious
 * reason that it does not look like our application, and for a better one: it promises a
 * redirect and offers nothing when the redirect does not arrive, which is the only case
 * in which anyone reads it.
 *
 * The library renders this on its `loginAsync_begin` event and passes it nothing but a
 * configuration name, so the retry comes from `useOidc`. That works here because the hook
 * resolves its client from a module-level registry rather than from React context, and
 * this component is mounted by the provider rather than inside its tree. The provider it
 * names comes from `getActiveProvider()` for the same reason: the props carry nothing else.
 */
export function RedirectingToSignIn({ configurationName }: { configurationName?: string }) {
  const { login } = useOidc(configurationName);
  const [stuck, setStuck] = useState(false);
  const idp = idpName(getActiveProvider());

  useEffect(() => {
    const timer = window.setTimeout(() => setStuck(true), STUCK_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className={styles.view}>
      <img className={styles.logo} src={`${import.meta.env.BASE_URL}dhi-logo.png`} alt="DHI" />
      <div className={styles.content}>
        <h1 className={styles.title}>
          Docs<em className={styles.titleAccent}> AI</em>
        </h1>
        <p className={styles.subtitle}>Taking you to {idp} to sign in...</p>
        {stuck && (
          <button type="button" className={styles.inlineAction} onClick={() => void login()}>
            Not redirected? Sign in again.
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * How each provider is described on its own sign-in screen.
 *
 * Nobody chooses between them any more, so nothing here says who a provider is for. The
 * footnote answers the question that screen does raise, which is why sign-in behaves as
 * it does. `idp` is the name of the page the redirect and the sign-out tab actually land
 * on, which is what the notices about those two have to call it: a colleague signs in and
 * out at Microsoft and has never heard of MIKE Cloud.
 */
const PROVIDER_COPY: Record<string, { footnote: string; idp: string }> = {
  [DHI_ENTRA]: {
    footnote: 'You are signing in with your DHI work account.',
    idp: 'Microsoft',
  },
  [MIKE_CLOUD]: {
    footnote: 'Powered by MIKE Cloud.',
    idp: 'MIKE Cloud',
  },
};

const FALLBACK_COPY = {
  footnote: 'Access is managed by your account.',
  idp: 'your sign-in provider',
};

function copyFor(provider: AuthProviderConfig) {
  return PROVIDER_COPY[provider.name] ?? FALLBACK_COPY;
}

/** What to call the identity provider's page, for screens that may have no provider yet. */
export function idpName(provider: AuthProviderConfig | null): string {
  return provider ? copyFor(provider).idp : FALLBACK_COPY.idp;
}

/**
 * How far the platform sign-out has got, or 'none' when it was never asked for.
 *
 * 'done' shows no message of its own. What it changes is what it takes away: the
 * remembered account and the button that leaves it, neither of which applies once the
 * session is gone. Saying so as well was telling the person something the screen in
 * front of them had already shown.
 */
export type PlatformExit = 'none' | 'opening' | 'done' | 'blocked';

/**
 * Where the platform half of a sign-out has got to, on whichever screen is showing.
 *
 * Extracted because two screens need it now. It used to live only on the per-provider
 * sign-in screen, which was also where a sign-out landed; a sign-out now returns to the
 * email step, and the instruction has to travel with it. Dropping it there would be the
 * worst kind of quiet bug: measured 2026-08-27, the platform's `/logout` lands on a page
 * that asks which account to sign out of and does nothing until one is clicked, so a
 * sign-out nobody is told to finish simply does not happen.
 */
function PlatformExitNotice({
  platformExit,
  provider,
  onReopen,
}: {
  platformExit: PlatformExit;
  /** Whose tab it is. Null only before a provider has been resolved, where nothing is open. */
  provider: AuthProviderConfig | null;
  onReopen: () => void;
}) {
  const idp = idpName(provider);
  return (
    <>
      {/* Not "it is happening", but "you have to do it". The one state where these screens
          have to ask rather than report, which is why it is drawn as a step with an
          instruction on its own line rather than as a grey aside. */}
      {platformExit === 'opening' && (
        <div className={styles.step} role="status">
          <p className={styles.stepLead}>Finish signing out in the {idp} tab</p>
          <p className={styles.stepDetail}>It asks which account to sign out of.</p>
          {/* For the tab that was closed too early, or that opened behind this window and
              was never seen. Reopening is the same call that opened it, so there is
              nothing to undo first. */}
          <button type="button" className={styles.inlineAction} onClick={onReopen}>
            Reopen that tab
          </button>
        </div>
      )}
      {/* What was actually observed, which is narrower than it reads: `done` fires when
          this document is looked at again, and `signOut.ts` closes the tab then "whether
          they completed it or gave up", so it is not evidence the session ended. */}
      {platformExit === 'done' && (
        <p className={styles.srOnly} role="status">
          The {idp} sign-out tab is closed.
        </p>
      )}
      {/* A failure rather than a pending step, and the accent rule turns amber to say so:
          nothing is under way, and nothing will be until pop-ups are allowed. */}
      {platformExit === 'blocked' && (
        <div className={`${styles.step} ${styles.stepBlocked}`} role="status">
          <p className={styles.stepLead}>Your browser blocked the {idp} sign-out page</p>
          <p className={styles.stepDetail}>
            {idp} still has this browser signed in. Allow pop-ups for this site, then try again.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * The way in: one field, one button, and the address decides which provider follows.
 *
 * It replaced a screen that asked people to pick between "your DHI account" and "MIKE
 * Cloud". Two things were wrong with that. It put an internal staff route on a
 * customer-facing page, as the primary button, because provider order came from
 * `AUTH_PROVIDER` and that had been ordered by audience size. And the distinction it asked
 * a customer to make has no visible consequence to them: both providers redirect to a
 * Microsoft page, and on the MIKE Cloud path that page names neither DHI, nor MIKE, nor
 * the tenant. We were asking for a classification only we could act on.
 *
 * Most people never see this. A browser that has signed in once has a remembered provider
 * and skips it, and a link carrying `?provider=` skips it too, which is what the welcome
 * email is for. So this is the cold-start path, and it is meant to be unremarkable.
 *
 * There is no way around the routing rule on this screen either. It used to keep the two
 * provider buttons behind a "Having trouble?" link, as a safety net for a colleague at a
 * domain the rule did not list. That net was never where anyone fell: a wrongly routed
 * address is refused by the identity provider on its own domain, with no link back here,
 * so the person who needed the buttons never saw them, and everyone else was one click
 * from the staff route this screen exists to hide. The way around is now `/signin/<provider>`,
 * a link support can hand to the one person who needs it.
 *
 * Four details here are the difference between this pattern working and merely existing,
 * and each is invisible to whoever built it, because they use a mouse and know their own
 * address. It is a real form, so Enter submits. The field is `autocomplete="username"`,
 * which is the token password managers key on for an identifier step, rather than the
 * `email` that looks more correct and offers nothing. It autofocuses, because it is the
 * only thing to do on the page. And it validates on submit rather than per keystroke,
 * since nobody needs to be told their address is incomplete while they are still typing.
 *
 * There is deliberately nothing here explaining that Microsoft comes next. A line saying so
 * was written and then removed as unwanted noise on a screen whose whole point is to ask
 * one short question. The reason it existed is still real and now lives nowhere: a beta
 * participant who lands on an unbranded consumer Microsoft page with no warning reads it as
 * phishing, recorded from a real session. That reassurance belongs in the welcome email
 * instead, which is where somebody first learns the app exists.
 */
export function EmailSignIn({
  providers,
  onChoose,
  reason = 'new',
  platformExit = 'none',
  onReopenPlatformSignOut,
}: {
  providers: AuthProviderConfig[];
  /**
   * Called with the provider the address routes to, and the address itself, so the next
   * screen can hand it to an identity provider that honours a hint.
   */
  onChoose: (provider: AuthProviderConfig, email: string) => void;
  /**
   * Why the screen is being shown, which sets the line under the title.
   *
   * A sign-out used to land on the per-provider screen, which offered "Sign in" beside
   * "Use a different account", and that second button's only action was to open the
   * platform sign-out that had just been performed. Returning to the front door instead
   * makes the question disappear rather than answering it. An expired session lands here
   * too: with two providers this is the one screen for anybody not signed in.
   */
  reason?: LoginReason;
  platformExit?: PlatformExit;
  onReopenPlatformSignOut?: () => void;
}) {
  const viewRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fieldId = useId();
  const errorId = useId();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!looksLikeEmail(email)) {
      setError('Enter a complete email address.');
      return;
    }
    const provider = providerForEmail(email, providers);
    if (!provider) {
      // Only reachable on a deployment where no provider takes unrecognised domains,
      // which the backend has a test against. Nothing on this screen can fix that, so it
      // says the one thing that is true and leaves the support link below to do the rest.
      setError('We could not sign you in with this address.');
      return;
    }
    setError(null);
    onChoose(provider, email.trim());
  }

  return (
    <div className={styles.view} ref={viewRef}>
      <img className={styles.logo} src={`${import.meta.env.BASE_URL}dhi-logo.png`} alt="DHI" />
      <TopoBackground containerRef={viewRef} />

      <div className={styles.content}>
        <h1 className={styles.title}>
          Docs<em className={styles.titleAccent}> AI</em>
        </h1>
        <p className={styles.subtitle}>{SUBTITLE[reason]}</p>

        <ProviderError />

        {onReopenPlatformSignOut && (
          <PlatformExitNotice
            platformExit={platformExit}
            // The provider just signed out of. Still set: a sign-out lands here without a
            // page load, and only a reload forgets it.
            provider={getActiveProvider()}
            onReopen={onReopenPlatformSignOut}
          />
        )}

        <form className={styles.form} onSubmit={submit} noValidate>
          {/* Announced but not drawn. With one field on the page the placeholder says what
              it is for, and a label over it repeated the same word in a smaller font. Kept in
              the markup because a placeholder is not an accessible name once someone has
              started typing, and because it is what a password manager keys on. */}
          <label className={styles.srOnly} htmlFor={fieldId}>
            Email
          </label>
          <input
            id={fieldId}
            className={styles.input}
            // `type="email"` is here for the mobile keyboard rather than for validation:
            // the browser's own check is stricter than real addresses are, which is why
            // the form is `noValidate` and `looksLikeEmail` is the only gate.
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            // biome-ignore lint/a11y/noAutofocus: the rule guards against stealing focus inside a page of content; this page is one field and nothing else, so not focusing it just makes everyone reach for the mouse first
            autoFocus
            placeholder="Email address"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              // Cleared on edit rather than revalidated per keystroke: by the time
              // somebody is fixing the address they have read the message.
              if (error) setError(null);
            }}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
          />
          {error && (
            <p className={styles.fieldError} id={errorId} role="alert">
              {error}
            </p>
          )}
          <button type="submit" className={styles.button}>
            Continue
          </button>
        </form>

        <p className={styles.footnote}>
          Access is by invitation. Trouble signing in?{' '}
          <a className={styles.link} href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            Contact DHI support
          </a>
        </p>
      </div>
    </div>
  );
}

export type LoginReason = 'new' | 'expired' | 'signed-out';

const SUBTITLE: Record<LoginReason, string> = {
  new: 'Documentation assistant for DHI software products',
  expired: 'Your session has ended. Sign in again to continue where you left off.',
  'signed-out': 'You are signed out of DHI Docs AI.',
};

type Props = {
  reason?: LoginReason;
  /** The provider this page signs in against, which decides most of the wording. */
  provider: AuthProviderConfig;
  /** Offered only where there is more than one way in. Sends the person back to the choice. */
  onUseDifferentProvider?: (() => void) | null;
  /**
   * The address the one-click path would sign in as, when we know it. Naming it is the
   * whole point: the platform will not ask, so this screen has to.
   */
  account?: string | null;
  platformExit?: PlatformExit;
  onSignIn: () => void | Promise<unknown>;
  /** Reopens the identity provider's sign-out tab when the notice about it asks for that. */
  onReopenPlatformSignOut: () => void;
};

/**
 * Our sign-in page, deliberately ours rather than the platform's.
 *
 * Signing in here can be silent. While the identity provider's own cookie is alive,
 * one click signs you in as whoever used the browser last with nothing on screen to say
 * so. On MIKE Cloud there is no way to change that: `/authorize` never reaches Microsoft
 * and the platform ignores every parameter that would make it ask who you are. So the
 * button label names the account when we know it: "Continue as <address>" is the only
 * place the identity appears, which makes it load-bearing rather than decorative.
 *
 * There used to be a second button here that ended the platform session, for the person
 * that label names wrongly. It went because it read as a second way in that needed
 * explaining, and the person it was for can sign out from the app menu instead, which
 * ends both sessions. What that leaves uncovered is a platform cookie for an account the
 * site has never admitted: that person is refused on the platform's own domain, and the
 * fix is signing out at MIKE Cloud itself, which is a support conversation rather than a
 * control on this screen.
 *
 * There is deliberately no email field, though the visual design has room for one. Nothing
 * typed here could reach the identity provider: `/authorize` redirects straight to
 * Microsoft and forwards neither `login_hint` nor any `prompt`, so the address would be
 * asked for again on the next screen and the field would be a small lie.
 *
 * It also renders the `error` query parameter when the provider redirects back with one.
 * Without that, a rejected login lands on a blank sign-in screen that looks identical to
 * a fresh visit, and the person has no idea why nothing happened. The platform's worst
 * case does not reach here at all: someone who is not a member of the site gets an
 * unbranded HTTP 500 on the platform's own domain, which our application never sees.
 */
/**
 * The identity provider's refusal, when the URL carries one.
 *
 * Read here rather than passed in, because it can land on either screen: the callback
 * returns to wherever the sign-in started, which is the per-provider screen when a
 * button was pressed and the email step when a silent resume left from an app path.
 */
function ProviderError() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error_description') || params.get('error');
  if (!error) return null;
  return (
    <div className={styles.error} role="alert">
      <p className={styles.errorLead}>
        We could not sign you in. Try again, and if it keeps happening,{' '}
        <a className={styles.link} href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
          contact DHI support
        </a>
        .
      </p>
      {/* The provider's own words, one click away rather than in the customer's face.
          They arrive as things like "AADSTS50105: ... Contact your administrator", which
          names an error code and an administrator an external customer does not have,
          and reads as our application breaking rather than as a permission it never had.
          Kept because it is the only diagnostic anyone gets: the failure happens off our
          origin and never reaches our logs. */}
      <details className={styles.errorDetails}>
        <summary>Technical details</summary>
        <p className={styles.errorRaw}>{error}</p>
      </details>
    </div>
  );
}

export default function LoginScreen({
  reason = 'new',
  provider,
  account = null,
  platformExit = 'none',
  onSignIn,
  onReopenPlatformSignOut,
  onUseDifferentProvider = null,
}: Props) {
  const [busy, setBusy] = useState(false);
  const viewRef = useRef<HTMLDivElement | null>(null);
  // After a deliberate sign-out the platform session is gone, so there is no account to
  // continue as even if we still remembered one.
  const returning = account && platformExit === 'none';

  return (
    <div className={styles.view} ref={viewRef}>
      <img className={styles.logo} src={`${import.meta.env.BASE_URL}dhi-logo.png`} alt="DHI" />
      <TopoBackground containerRef={viewRef} />

      <div className={styles.content}>
        <h1 className={styles.title}>
          Docs<em className={styles.titleAccent}> AI</em>
        </h1>
        <p className={styles.subtitle}>{SUBTITLE[reason]}</p>

        <ProviderError />

        <PlatformExitNotice
          platformExit={platformExit}
          provider={provider}
          onReopen={onReopenPlatformSignOut}
        />

        <button
          type="button"
          className={
            platformExit === 'opening' ? `${styles.button} ${styles.buttonQuiet}` : styles.button
          }
          disabled={busy}
          onClick={() => {
            setBusy(true);
            // The sign-in leaves the page, so nothing resets this; it exists to stop a
            // second click firing a second authorize request during the redirect.
            void Promise.resolve(onSignIn()).catch(() => setBusy(false));
          }}
        >
          {busy ? 'Signing in...' : returning ? `Continue as ${account}` : 'Sign in'}
        </button>

        {/* Only where there is somewhere else to go. Its real job is the person who
            picked wrong once and would otherwise be stuck with the choice for as long as
            the browser remembers it. */}
        {onUseDifferentProvider && (
          <button type="button" className={styles.inlineAction} onClick={onUseDifferentProvider}>
            Sign in a different way
          </button>
        )}

        <p className={styles.footnote}>
          {copyFor(provider).footnote}{' '}
          <a className={styles.link} href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            Trouble signing in?
          </a>
        </p>
      </div>
    </div>
  );
}
